import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import { useCreateAnswerMutation, useUpdateAnswerMutation } from 'api/main';
import { customTunnelSelectors } from 'reducers';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomTextField, Loader } from 'components';
import { Box } from '@mui/material';
import * as icons from 'mdi-material-ui';
import { customTunnelSchemas } from 'schemas';

const schema = {
    title: Joi.string().required().trim().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    score: Joi.number().required().messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
};

const AnswerForm = ({ onUpdate, onCancel, answer, pmAnswerId, index }) => {
    const { ppId: ppQuestionId, pmId: pmQuestionId } = useSelector(
        customTunnelSelectors.getEditingQuestionId
    );

    const [form, setForm] = useState(answer);
    const [errors, setErrors] = useState({ title: '', score: '' });

    const [updateAnswer, { isLoading: updateLoading }] = useUpdateAnswerMutation();
    const [createAnswer, { isLoading: createLoading }] = useCreateAnswerMutation();

    const handleChange = useCallback(
        key => e => {
            setErrors(prevState => ({ ...prevState, [key]: '' }));
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({ title: '', score: '' });
            if (answer?.id) {
                // update an existing answer from the DB
                updateAnswer(
                    customTunnelSchemas.updateAnswer({ ppId: answer.id, pmId: pmAnswerId, form })
                );
            } else if (ppQuestionId) {
                // create an answer for an existing question
                createAnswer(
                    customTunnelSchemas.createAnswer({ ppQuestionId, pmQuestionId, form, index })
                );
            } else {
                // update the answer in the question form
                onUpdate({ ...form, score: +form.score, order: index + 1 });
            }
        } else {
            setErrors(newErrors);
        }
    }, [
        answer.id,
        form,
        index,
        onUpdate,
        pmAnswerId,
        ppQuestionId,
        pmQuestionId,
        updateAnswer,
        createAnswer,
    ]);

    return (
        <Box padding={1} width="100%">
            <CustomTextField
                label={texts.ANSWER_TEXT}
                onChange={handleChange('title')}
                value={form.title}
                error={errors.title}
            />
            <CustomTextField
                type="number"
                allowNegative
                label={texts.ANSWER_SCORE}
                onChange={handleChange('score')}
                value={form.score}
                error={errors.score}
            />
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <icons.CloseThick
                    sx={{ cursor: 'pointer', '&:hover': { color: 'error.main' } }}
                    onClick={onCancel}
                />
                <Loader loading={updateLoading || createLoading} size={20} marginTop={0}>
                    <icons.CheckBold
                        sx={{ cursor: 'pointer', '&:hover': { color: 'success.main' } }}
                        onClick={handleSubmit}
                    />
                </Loader>
            </Box>
        </Box>
    );
};

AnswerForm.propTypes = {
    answer: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    pmAnswerId: PropTypes.number,
    index: PropTypes.number,
};

export default AnswerForm;
